/** "生成创意" (`generate_creative`) — the unified Vistoera Generation Node
 * (VISTOERA_CANVAS_PROJECT_START_TO_GENERATE_CREATIVE_UNIFIED_GENERATION_
 * NODE_V2). Collapses V1's `project_start` (Brief SOURCE) +
 * `creative_candidate` (RESULT ×3, own Canvas nodes) two-node model into
 * ONE card: §6's "生成类节点 = 生成器 + 当前结果载体" —
 *
 *   TOP    = current/future result (empty / loading / result / error)
 *   BOTTOM = input & controls (Brief, Structured Context Assets, Skill,
 *            Provider, Generate/Regenerate, Diverge)
 *
 * Reuses `ProjectStartNode.tsx`'s Brief-field UI almost verbatim (same
 * field names, same "only Creative Intent is required" rule, §7) — this
 * file is NOT a rewrite of that node (which stays fully intact for
 * legacy Canvas compat, spec §15), it is a NEW sibling that assembles
 * the same building blocks into the new unified layout plus the result/
 * history/diverge pieces V1 never needed on this node itself. */

import {
  BranchesOutlined,
  CloseOutlined,
  CopyOutlined,
  DownOutlined,
  FileDoneOutlined,
  HistoryOutlined,
  LoadingOutlined,
  PlusOutlined,
  RightOutlined,
} from '@ant-design/icons'
import { Handle, Position, type NodeProps } from '@xyflow/react'
import { Tooltip } from 'antd'
import CanvasSelect from '../CanvasSelect'
import { memo, useCallback, useEffect, useMemo, useState, type DragEvent as ReactDragEvent, type KeyboardEvent, type MouseEvent as ReactMouseEvent } from 'react'
import {
  CREATIVE_IDEATION_CAPABILITY,
  getCreativeIdeationNodeHistory,
  type CreativeIdeationHistoryEntry,
  type ImageAssetPickerSelection,
} from '../../../../api'
import CanvasActionPopover from '../CanvasActionPopover'
import CanvasFunctionCardHeader from '../CanvasFunctionCardHeader'
import { patchCanvasFieldsIfChanged } from '../canvasFieldsEqual'
import {
  buildExplicitProviderSelectionPatch,
  isProviderOptionSelectable,
  normalizeProviderSelectionMode,
  resolveCapabilityProviderOptionId,
} from '../capabilityProviderSelection'
import { loadImagePickerItemDetailDeduped } from '../canvasSharedConfigLoaders'
import { isBriefAffectingPatch } from '../creativeIdeationCanvas'
import { functionCardShowsInspector } from '../functionCardInteractionV2'
import { NODE_EXPAND_TRIGGER_CLASS, canvasNodeShellStateClass } from '../canvasNodeShellV2'
import type { GenerateCreativeResult } from '../generateCreativeCanvas'
import { useNodeDefinition } from '../nodeRegistry'
import { useCapabilityProviderOptions } from '../useCapabilityProviderOptions'
import type { FlowNodeData } from '../types'
import { buildCreativePayloadFromNode } from '../canvasInputBinding'
import { CREATIVE_CONTEXT_DND_TYPE } from './CreativeCandidateNode'
import { vistoeraDndProps } from '../canvasDragContract'
import { NODE_CONTEXT_DND_HANDLE_CLASS, NODE_PRESENTATION_SURFACE_CLASS } from '../nodeInteractionContract'
import ImageThumbnailHoverPreview from './ImageThumbnailHoverPreview'
import CanvasResultQuickActionRail from './CanvasResultQuickActionRail'

// VISTOERA_GENERATE_CREATIVE_CANONICAL_CARD_VISUAL_COLLAPSE_EXPAND_
// AND_DIVERGE_UI_V4 §34 — collapsed is Result-first compact; expanded
// matches Storyboard's Wide Card sizing band (~720–820).
import { generationNodeCardWidth } from '../generationNodeWidthPresets'

const NODE_CARD_WIDTH = generationNodeCardWidth('generate_creative')

const INTENT_MAX_LENGTH = 800

const INTENT_EXAMPLES = [
  '给这个产品想几个创意',
  '做一个搞笑短片',
  '我不知道拍什么，帮我想',
  '突出品质感与情感共鸣',
]

const CONTENT_GOAL_OPTIONS: string[] = ['品牌广告', '产品上新', 'IP短剧', '知识内容', '搞笑娱乐', '种草推荐', '品牌故事', '连续剧', '自由创作', '其他']

const DURATION_CHIPS: Array<{ key: string; label: string; seconds: number | null }> = [
  { key: '15', label: '15秒', seconds: 15 },
  { key: '30', label: '30秒', seconds: 30 },
  { key: '60', label: '60秒', seconds: 60 },
  { key: '120', label: '1-3分钟', seconds: 120 },
]

const PLATFORM_OPTIONS = ['抖音', '小红书', 'YouTube', 'TikTok', 'Instagram', '其他']

interface AssetRef {
  asset_id: string
  picker_item_id?: string
  asset_type: string
  display_name: string
}

function formatDuration(seconds: number | null | undefined): string | null {
  if (!seconds || seconds <= 0) return null
  if (seconds < 60) return `${Math.round(seconds)}秒`
  const minutes = seconds / 60
  return `${minutes % 1 === 0 ? minutes : minutes.toFixed(1)}分钟`
}

function TagListEditor({ values, placeholder, onChange }: { values: string[]; placeholder: string; onChange: (next: string[]) => void }) {
  const [draft, setDraft] = useState('')
  const commit = useCallback(() => {
    const v = draft.trim()
    if (!v) return
    if (!values.includes(v)) onChange([...values, v])
    setDraft('')
  }, [draft, onChange, values])
  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      commit()
    }
  }
  return (
    <div className="pcv-brief-section nodrag">
      <div className="pcv-brief-chip-row">
        {values.map((v) => (
          <span key={v} className="pcv-brief-chip is-active">
            {v}
            <CloseOutlined style={{ marginLeft: 5, fontSize: 9 }} onClick={() => onChange(values.filter((x) => x !== v))} />
          </span>
        ))}
      </div>
      <input
        className="pcv-brief-textarea nodrag"
        style={{ minHeight: 30, height: 30, padding: '5px 8px' }}
        value={draft}
        placeholder={placeholder}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={onKeyDown}
        onBlur={commit}
      />
    </div>
  )
}

function AssetChip({ asset, workspaceId, onRemove }: { asset: AssetRef; workspaceId: string; onRemove: () => void }) {
  const [thumb, setThumb] = useState<string | null>(null)
  useEffect(() => {
    let cancelled = false
    if (asset.picker_item_id && workspaceId) {
      loadImagePickerItemDetailDeduped(workspaceId, asset.picker_item_id, { include_usage: false, include_variants: false })
        .then((d) => {
          if (!cancelled) setThumb(d.thumbnail_url || d.preview_url || null)
        })
        .catch(() => {})
    }
    return () => {
      cancelled = true
    }
  }, [asset.picker_item_id, workspaceId])
  return (
    <span className="pcv-brief-asset-chip nodrag" title={asset.display_name}>
      <ImageThumbnailHoverPreview thumbUrl={thumb} />
      <span className="pcv-brief-asset-name">{asset.display_name}</span>
      <button type="button" className="pcv-brief-asset-remove" aria-label="移除" onClick={onRemove}>
        <CloseOutlined />
      </button>
    </span>
  )
}

function HistoryList({ entries }: { entries: CreativeIdeationHistoryEntry[] }) {
  if (entries.length === 0) {
    return <div className="pcv-gen-history-empty">暂无历史版本</div>
  }
  return (
    <div className="pcv-gen-history-list">
      {entries.map((h, idx) => (
        <div key={h.run_id} className="pcv-gen-history-item">
          <span className="pcv-gen-history-version">V{entries.length - idx}</span>
          <div className="pcv-gen-history-body">
            <div className="pcv-gen-history-title">{h.title || (h.status === 'ERROR' ? '生成失败' : '生成中…')}</div>
            {h.premise ? <div className="pcv-gen-history-premise">{h.premise}</div> : null}
            <div className="pcv-gen-history-meta">
              <span>{h.run_kind === 'EXPAND' ? '发散' : '生成'}</span>
              <span>{h.status}</span>
              <span>{h.created_at ? new Date(h.created_at).toLocaleString() : ''}</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

function GenerateCreativeNodeImpl({ id, data, selected }: NodeProps) {
  const nodeData = data as unknown as FlowNodeData & { workspaceId?: string; projectId?: string }
  const def = useNodeDefinition(nodeData.nodeType)
  const workspaceId = nodeData.workspaceId || ''
  const fields = nodeData.fields || {}
  const isDetailExpanded = Boolean(nodeData.isDetailExpanded)
  // V4 §17–§18 — Collapsed = Header + Result ONLY. Assets / Parameters /
  // Prompt / Footer are expanded-only (COLLAPSED_SHOWS_FOOTER=NO). Same
  // accordion helper Storyboard / other generation cards already use.
  const showExpanded = functionCardShowsInspector(isDetailExpanded, nodeData.isDragTempCollapsed)

  const userIntent = String(fields.userIntent ?? '')
  const contentGoal = (fields.contentGoal as string | null) ?? null
  const targetDurationSeconds = (fields.targetDurationSeconds as number | null) ?? null
  const durationSource = (fields.durationSource as string) || 'DEFAULT_UI_VALUE'
  const assets = Array.isArray(fields.assets) ? (fields.assets as AssetRef[]) : []
  const mustInclude = Array.isArray(fields.mustInclude) ? (fields.mustInclude as string[]) : []
  const mustAvoid = Array.isArray(fields.mustAvoid) ? (fields.mustAvoid as string[]) : []
  const audience = (fields.audience as string) || ''
  const platform = (fields.platform as string | null) ?? null
  const tone = (fields.tone as string) || ''
  const references = Array.isArray(fields.references) ? (fields.references as string[]) : []
  const skillSelectionSource = (fields.skillSelectionSource as string) || 'AUTO'
  const providerSelectionMode = normalizeProviderSelectionMode(fields.providerSelectionMode)
  const storedProviderOptionId = (fields.providerOptionId as string | null) ?? null

  const currentResult = (fields.currentResult as GenerateCreativeResult | null) ?? null
  const runStatus = (fields.runStatus as string | null) ?? null
  const runErrorMessage = (fields.runErrorMessage as string | null) ?? null
  const divergeRunStatus = (fields.divergeRunStatus as string | null) ?? null
  const divergeRunErrorMessage = (fields.divergeRunErrorMessage as string | null) ?? null
  const generationDepth = (fields.generationDepth as number) || 0
  const siblingIndex =
    typeof fields.divergenceSiblingIndex === 'number'
      ? (fields.divergenceSiblingIndex as number)
      : typeof fields.siblingIndex === 'number'
        ? (fields.siblingIndex as number)
        : null

  const [moreOpen, setMoreOpen] = useState(Boolean(fields.moreRequirementsExpanded))
  const [customDurationDraft, setCustomDurationDraft] = useState('')
  const [divergeOpen, setDivergeOpen] = useState(false)
  const [divergeInstruction, setDivergeInstruction] = useState('')
  const [historyOpen, setHistoryOpen] = useState(false)
  const [historyEntries, setHistoryEntries] = useState<CreativeIdeationHistoryEntry[] | null>(null)
  const [historyLoading, setHistoryLoading] = useState(false)
  const [divergeBtnEl, setDivergeBtnEl] = useState<HTMLButtonElement | null>(null)
  const [historyBtnEl, setHistoryBtnEl] = useState<HTMLButtonElement | null>(null)
  const [resultHovered, setResultHovered] = useState(false)

  const { options: providerOptions, error: providerOptionsError } = useCapabilityProviderOptions(
    workspaceId,
    CREATIVE_IDEATION_CAPABILITY,
    'generate_creative',
  )
  const resolvedProviderOptionId = useMemo(
    () => resolveCapabilityProviderOptionId({ selectionMode: providerSelectionMode, storedProviderOptionId, options: providerOptions }),
    [providerOptions, providerSelectionMode, storedProviderOptionId],
  )

  const isGenerateBusy = runStatus === 'SUBMITTING' || runStatus === 'QUEUED' || runStatus === 'GENERATING'
  const isDivergeBusy = divergeRunStatus === 'SUBMITTING' || divergeRunStatus === 'QUEUED' || divergeRunStatus === 'GENERATING'
  // §20 Diverge = "Regenerate current branch + create two sibling
  // branches" — while a Diverge run is in flight THIS node's own result
  // area must ALSO show the generating state (its old result is
  // temporarily hidden, exactly like a plain Regenerate — restored
  // automatically on ERROR since `currentResult` itself is never
  // touched until SUCCESS). §24/§39/§47 delete/duplicate lock keys off
  // this SAME combined flag on every one of the 3 divergence-group
  // members.
  const isBusy = isGenerateBusy || isDivergeBusy

  const patchFields = useCallback(
    (patch: Record<string, unknown>) => {
      const next = patchCanvasFieldsIfChanged(fields, patch)
      if (!next) return
      nodeData.onChangeFields?.(id, next)
      if (isBriefAffectingPatch(patch)) {
        nodeData.onGenerateCreativeBriefChanged?.(id)
      }
    },
    [fields, id, nodeData],
  )

  const appendExample = (text: string) => {
    const current = userIntent.trim()
    const next = current ? `${current}\n${text}` : text
    patchFields({ userIntent: next.slice(0, INTENT_MAX_LENGTH) })
  }

  const pickDuration = (chip: (typeof DURATION_CHIPS)[number]) => {
    patchFields({ targetDurationSeconds: chip.seconds, durationSource: 'USER_CONFIRMED_VALUE' })
  }
  const pickAiSuggestedDuration = () => {
    patchFields({ targetDurationSeconds: null, durationSource: 'AI_SUGGESTED' })
  }
  const commitCustomDuration = () => {
    const n = Number(customDurationDraft)
    if (Number.isFinite(n) && n > 0) {
      patchFields({ targetDurationSeconds: Math.round(n), durationSource: 'USER_CONFIRMED_VALUE' })
    }
    setCustomDurationDraft('')
  }
  const isCustomDurationActive =
    durationSource === 'USER_CONFIRMED_VALUE' && targetDurationSeconds != null && !DURATION_CHIPS.some((c) => c.seconds === targetDurationSeconds)

  const openAssetPicker = () => {
    nodeData.onOpenAssetPicker?.({
      nodeId: id,
      purpose: 'generic',
      title: '添加关联资产',
      selectionMode: 'multiple',
      maxSelection: 20,
      initialSelection: assets.map((a) => a.picker_item_id || `sa:${a.asset_id}`),
      onConfirm: (selections: ImageAssetPickerSelection[]) => {
        const next: AssetRef[] = selections.map((s) => ({
          asset_id: s.assetId,
          picker_item_id: s.pickerItemId,
          asset_type: 'image_asset',
          display_name: s.filename || s.assetId,
        }))
        if (nodeData.onBindRelatedAssetsFromPicker) {
          nodeData.onBindRelatedAssetsFromPicker(id, next)
          return
        }
        patchFields({ assets: next })
      },
    })
  }
  const removeAsset = (assetId: string) => {
    if (nodeData.onUnbindRelatedAsset) {
      nodeData.onUnbindRelatedAsset(id, assetId)
      return
    }
    patchFields({ assets: assets.filter((a) => a.asset_id !== assetId) })
  }

  const providerSelectOptions = useMemo(
    () =>
      providerOptions.map((opt) => ({
        value: opt.provider_option_id,
        disabled: !isProviderOptionSelectable(opt),
        label: opt.frontend_display_name,
        title: opt.frontend_description || opt.frontend_display_name,
      })),
    [providerOptions],
  )

  const generateStatusLabel = useMemo(() => {
    const status = isDivergeBusy ? divergeRunStatus : runStatus
    if (status === 'SUBMITTING') return '正在提交…'
    if (status === 'QUEUED') return '排队中…'
    if (status === 'GENERATING') return currentResult ? '正在重新生成…' : '正在生成创意…'
    return null
  }, [currentResult, divergeRunStatus, isDivergeBusy, runStatus])

  const canGenerate = userIntent.trim().length > 0 && !isBusy
  // §22 — Diverge no longer requires an existing result; an empty node
  // can still Diverge straight from its Brief (all 3 siblings generate
  // from scratch).
  const canDiverge = userIntent.trim().length > 0 && !isBusy
  const canDuplicate = !isBusy
  // P0: delete allowed while generating — background run continues; success → asset + bill.

  const openHistory = (el: HTMLButtonElement | null) => {
    setHistoryBtnEl(el)
    setHistoryOpen(true)
    if (historyEntries === null && workspaceId) {
      setHistoryLoading(true)
      getCreativeIdeationNodeHistory(workspaceId, id)
        .then((entries) => setHistoryEntries(entries))
        .catch(() => setHistoryEntries([]))
        .finally(() => setHistoryLoading(false))
    }
  }

  // A fresh Generate/Regenerate/Diverge completing should invalidate any
  // already-fetched history snapshot (V2 §11 "重新打开后历史不能丢失" —
  // the inverse case: an ALREADY-open snapshot must not go stale either).
  useEffect(() => {
    setHistoryEntries(null)
  }, [runStatus, divergeRunStatus])

  const submitDiverge = (instruction: string | null) => {
    setDivergeOpen(false)
    setDivergeInstruction('')
    nodeData.onDivergeCreative?.(id, instruction)
  }

  // §3/§39/§40 Header actions — Duplicate/Delete live HERE ONLY, never
  // duplicated into the Footer (§11/§14 "一个动作只有一个 canonical
  // 位置"). Both route through the SAME generic `onDuplicateNode`/
  // `onRequestDelete` contract every other generation node family
  // already uses (`ScriptGenerationNode.tsx`, `GenerateImageNode.tsx`,
  // …) — this node just needed to start calling them.
  const handleDuplicate = (e: ReactMouseEvent) => {
    e.stopPropagation()
    if (!canDuplicate) return
    nodeData.onDuplicateNode?.(id)
  }
  const handleDelete = (e: ReactMouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    nodeData.onRequestDelete?.(id, e.currentTarget)
  }
  const handleResultExpandClick = () => {
    nodeData.onExpandFunctionCardDetail?.(id)
  }
  // §5/§6/§35/§36 Result Area hover Quick Action "生成剧本".
  const handleQuickCreateScript = (e: ReactMouseEvent) => {
    e.stopPropagation()
    nodeData.onQuickCreateScriptFromCreative?.(id)
  }

  const durationLabel = formatDuration(currentResult?.estimatedDuration ?? null)
  const resultTags = useMemo(() => {
    const tags: string[] = []
    for (const t of currentResult?.tags || []) {
      if (t && !tags.includes(t)) tags.push(t)
    }
    if (currentResult?.tone && !tags.includes(currentResult.tone)) tags.push(currentResult.tone)
    if (durationLabel && !tags.includes(durationLabel)) tags.push(durationLabel)
    return tags
  }, [currentResult, durationLabel])

  // VISTOERA_CANVAS_SCRIPT_GENERATION_NODE_AND_CREATIVE_ASSET_CONTEXT_
  // LINKING_V1 §3 — drag THIS node's own `fields.currentResult` out onto
  // a `script_generation` node's drop target. Same payload builder as
  // handle-connect / Quick Action (EDGE ⇔ CONTEXT contract).
  const onDragStartCreativeContext = (e: ReactDragEvent) => {
    if (!currentResult) return
    const payload = buildCreativePayloadFromNode({
      id,
      data: { nodeType: 'generate_creative', fields: nodeData.fields || {} },
    } as never)
    if (!payload) return
    e.dataTransfer.setData(CREATIVE_CONTEXT_DND_TYPE, JSON.stringify(payload))
    e.dataTransfer.effectAllowed = 'copy'
  }

  const cardWidth = NODE_CARD_WIDTH
  const resultStatusBadge = isBusy ? '生成中' : currentResult ? '已生成' : '未生成'

  // V4 canonical vertical order — ① Header ② Result always; ③ Assets
  // ④ Parameters ⑤ Prompt ⑥ Footer only when expanded (§17–§18).
  return (
    <div
      className={`pcv-node pcv-node--generate-creative${selected ? ' is-selected' : ''} ${canvasNodeShellStateClass(showExpanded)}${resultHovered ? ' is-result-zone-active' : ''}`}
      style={{ width: cardWidth }}
      data-testid="canvas-generate-creative-node"
    >
      {/* Always expose a target handle so image_asset → generate_creative
       * connections work on generationDepth=0 nodes (ingestion P0). */}
      <Handle type="target" position={Position.Left} className="pcv-handle" />
      <Handle type="source" position={Position.Right} className="pcv-handle" />

      {/* ① Header — icon + title only; RIGHT History|Duplicate|Delete */}
      <CanvasFunctionCardHeader
        icon={def.icon}
        color={def.color}
        title={def.label}
        actions={
          <>
            <Tooltip title="历史版本" mouseEnterDelay={0.3}>
              <button
                type="button"
                ref={setHistoryBtnEl}
                className="pcv-media-hover-btn"
                onClick={(e) => {
                  e.stopPropagation()
                  openHistory(e.currentTarget)
                }}
              >
                <HistoryOutlined />
              </button>
            </Tooltip>
            <Tooltip title={isBusy ? '正在生成，完成后可以复制' : '复制节点'} mouseEnterDelay={0.3}>
              <button type="button" className="pcv-media-hover-btn" disabled={!canDuplicate} onClick={handleDuplicate}>
                <CopyOutlined />
              </button>
            </Tooltip>
            <Tooltip title={'删除节点'} mouseEnterDelay={0.3}>
              <button type="button" className="pcv-media-hover-btn pcv-media-hover-btn--danger" onClick={handleDelete}>
                <CloseOutlined />
              </button>
            </Tooltip>
          </>
        }
      />

      {/* ② Result — ALWAYS visible. Taller Result-first surface (§14–§16). */}
      <div
        className={`pcv-gen-result-area pcv-gen-creative-result-area ${NODE_EXPAND_TRIGGER_CLASS}`}
        onMouseEnter={() => setResultHovered(true)}
        onMouseLeave={() => setResultHovered(false)}
        onClick={handleResultExpandClick}
      >
        <div className="pcv-gen-result-section-label">
          <span>生成结果</span>
          {generationDepth > 0 || (siblingIndex != null && siblingIndex >= 0) ? (
            <span className="pcv-gen-result-meta-badges">
              {generationDepth > 0 ? <span className="pcv-brief-header-badge">深度 {generationDepth}</span> : null}
              {siblingIndex != null && siblingIndex >= 0 ? (
                <span className="pcv-brief-header-badge">方向 {String.fromCharCode(65 + siblingIndex)}</span>
              ) : null}
            </span>
          ) : null}
          <span className={`pcv-gen-result-status-badge${currentResult && !isBusy ? ' is-ready' : ''}${isBusy ? ' is-busy' : ''}`}>
            {resultStatusBadge}
          </span>
        </div>
        {!currentResult && !isBusy ? (
          <div className="pcv-gen-result-empty">
            <span className="pcv-gen-result-empty-icon">{def.icon}</span>
            <span>还没有创意结果</span>
            <span className="pcv-gen-result-empty-hint">
              {showExpanded ? '填写创作意图，点击「生成创意」' : '展开卡片填写创作意图，再点击「生成创意」'}
            </span>
          </div>
        ) : null}
        {isBusy ? (
          <div className="pcv-gen-result-loading">
            <LoadingOutlined spin />
            <span>{generateStatusLabel}</span>
          </div>
        ) : null}
        {!isBusy && currentResult ? (
          <div className={`pcv-gen-result-current ${NODE_PRESENTATION_SURFACE_CLASS}`}>
            {currentResult.stale ? <div className="pcv-candidate-stale-banner">创作要求已更新，建议重新生成</div> : null}
            <div className="pcv-gen-result-title-row">
              <div className="pcv-gen-result-title">{currentResult.title}</div>
              <span className="pcv-gen-result-main-tag">主创意</span>
              <FileDoneOutlined
                className={`pcv-candidate-drag-hint ${NODE_CONTEXT_DND_HANDLE_CLASS}`}
                {...vistoeraDndProps('creative-context')}
                draggable
                onDragStart={onDragStartCreativeContext}
                title="拖拽到「生成剧本」节点，作为其创意上下文"
              />
            </div>
            {currentResult.premise ? <div className="pcv-gen-result-premise">{currentResult.premise}</div> : null}
            {currentResult.hook ? <div className="pcv-gen-result-hook">“{currentResult.hook}”</div> : null}
            {showExpanded && (currentResult.productRole || currentResult.characterRole || currentResult.storyDirection) ? (
              <div className="pcv-gen-result-summary-row">
                {currentResult.characterRole ? <span>角色：{currentResult.characterRole}</span> : null}
                {currentResult.productRole ? <span>产品：{currentResult.productRole}</span> : null}
                {currentResult.storyDirection ? <span>方向：{currentResult.storyDirection}</span> : null}
              </div>
            ) : null}
            {resultTags.length > 0 ? (
              <div className="pcv-candidate-meta-row">
                {resultTags.slice(0, showExpanded ? 8 : 5).map((tag) => (
                  <span key={tag} className="pcv-candidate-tag">
                    {tag}
                  </span>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}
        {!isDivergeBusy && runStatus === 'ERROR' && runErrorMessage ? (
          <div className="pcv-gen-capability-warning pcv-gen-capability-warning--error">{runErrorMessage}</div>
        ) : null}
        {divergeRunStatus === 'ERROR' && divergeRunErrorMessage ? (
          <div className="pcv-gen-capability-warning pcv-gen-capability-warning--error">发散失败：{divergeRunErrorMessage}</div>
        ) : null}
      </div>

      {showExpanded ? (
        <div className="pcv-brief-body pcv-gen-input-body nodrag" onClick={(e) => e.stopPropagation()}>
          {/* ③ Structured Context Assets */}
          <div className="pcv-brief-section">
            <span className="pcv-brief-section-label">
              关联资产（{assets.length}） <span className="pcv-brief-section-optional">（可选）</span>
            </span>
            <div className="pcv-brief-asset-row">
              {assets.map((a) => (
                <AssetChip key={a.asset_id} asset={a} workspaceId={workspaceId} onRemove={() => removeAsset(a.asset_id)} />
              ))}
              <button type="button" className="pcv-brief-asset-add-btn nodrag" onClick={openAssetPicker}>
                <PlusOutlined /> 添加资产
              </button>
            </div>
          </div>

          {/* ④ Parameters — real schema only (§23). No fake 创意数量. */}
          <div className="pcv-gen-params-header">
            <span className="pcv-brief-section-label">生成参数</span>
            <button
              type="button"
              className="pcv-brief-collapse-toggle nodrag"
              onClick={() => {
                const next = !moreOpen
                setMoreOpen(next)
                patchFields({ moreRequirementsExpanded: next })
              }}
            >
              {moreOpen ? <DownOutlined /> : <RightOutlined />} 更多参数
            </button>
          </div>
          <div className="pcv-gen-params-primary">
            <div className="pcv-gen-field-row">
              <span className="pcv-gen-field-label">创意方向</span>
              <CanvasSelect
                size="small"
                className="pcv-gen-field-select nodrag"
                popupClassName="pcv-gen-field-select-popup"
                value={contentGoal ?? undefined}
                placeholder="AI 判断"
                allowClear
                options={CONTENT_GOAL_OPTIONS.map((label) => ({ value: label, label }))}
                onChange={(v) => patchFields({ contentGoal: v ?? null, contentGoalSource: v ? 'USER_CONFIRMED' : 'UNKNOWN' })}
                onClear={() => patchFields({ contentGoal: null, contentGoalSource: 'UNKNOWN' })}
                popupMatchSelectWidth={false}
                placement="bottomLeft"
                getPopupContainer={() => document.body}
              />
            </div>
            <div className="pcv-gen-field-row">
              <span className="pcv-gen-field-label">目标时长</span>
              <div className="pcv-brief-chip-row nodrag" style={{ margin: 0 }}>
                {DURATION_CHIPS.map((chip) => (
                  <span
                    key={chip.key}
                    className={`pcv-brief-chip${targetDurationSeconds === chip.seconds && durationSource !== 'AI_SUGGESTED' ? ' is-active' : ''}`}
                    onClick={() => pickDuration(chip)}
                  >
                    {chip.label}
                  </span>
                ))}
                <span className={`pcv-brief-chip${isCustomDurationActive ? ' is-active' : ''}`}>
                  <input
                    style={{ width: 40, border: 'none', background: 'none', color: 'inherit', outline: 'none', fontSize: 11 }}
                    placeholder="自定义"
                    value={customDurationDraft}
                    onChange={(e) => setCustomDurationDraft(e.target.value.replace(/[^0-9]/g, ''))}
                    onBlur={commitCustomDuration}
                    onKeyDown={(e) => e.key === 'Enter' && commitCustomDuration()}
                  />
                  秒
                </span>
                <span className={`pcv-brief-chip${durationSource === 'AI_SUGGESTED' ? ' is-active' : ''}`} onClick={pickAiSuggestedDuration}>
                  AI 建议
                </span>
              </div>
            </div>
          </div>

          {moreOpen ? (
            <div className="pcv-brief-more-body">
              <div className="pcv-gen-field-row">
                <span className="pcv-gen-field-label">风格参考</span>
                <input
                  className="pcv-brief-textarea nodrag"
                  style={{ minHeight: 30, height: 30, padding: '5px 8px' }}
                  value={tone}
                  placeholder="例如：电影质感、幽默、温暖"
                  onChange={(e) => patchFields({ tone: e.target.value })}
                />
              </div>
              <div className="pcv-gen-field-row">
                <span className="pcv-gen-field-label">受众人群</span>
                <input
                  className="pcv-brief-textarea nodrag"
                  style={{ minHeight: 30, height: 30, padding: '5px 8px' }}
                  value={audience}
                  placeholder="例如：25-40岁城市家庭"
                  onChange={(e) => patchFields({ audience: e.target.value })}
                />
              </div>
              <div className="pcv-gen-field-row">
                <span className="pcv-gen-field-label">发布平台</span>
                <CanvasSelect
                  size="small"
                  className="pcv-gen-field-select nodrag"
                  popupClassName="pcv-gen-field-select-popup"
                  value={platform ?? undefined}
                  allowClear
                  placeholder="不限"
                  options={PLATFORM_OPTIONS.map((p) => ({ value: p, label: p }))}
                  onChange={(v) => patchFields({ platform: v ?? null })}
                  onClear={() => patchFields({ platform: null })}
                  popupMatchSelectWidth={false}
                  placement="bottomLeft"
                  getPopupContainer={() => document.body}
                />
              </div>
              <div className="pcv-brief-section">
                <span className="pcv-brief-section-label">必须出现</span>
                <TagListEditor values={mustInclude} placeholder="输入后按 Enter 添加" onChange={(v) => patchFields({ mustInclude: v })} />
              </div>
              <div className="pcv-brief-section">
                <span className="pcv-brief-section-label">不能出现</span>
                <TagListEditor values={mustAvoid} placeholder="输入后按 Enter 添加" onChange={(v) => patchFields({ mustAvoid: v })} />
              </div>
              <div className="pcv-brief-section">
                <span className="pcv-brief-section-label">参考内容</span>
                <TagListEditor values={references} placeholder="输入内容/资产 ID 后按 Enter" onChange={(v) => patchFields({ references: v })} />
              </div>
            </div>
          ) : null}

          {/* ⑤ Prompt — last input section before Footer (§25). */}
          <div className="pcv-brief-section">
            <span className="pcv-brief-section-label">创作意图 / Prompt（必填）</span>
            <textarea
              className="pcv-brief-textarea nodrag"
              placeholder="例如：请结合产品的核心卖点，突出品质感和情感共鸣，提供一个电影感的创意方向……"
              value={userIntent}
              maxLength={INTENT_MAX_LENGTH}
              onChange={(e) => patchFields({ userIntent: e.target.value })}
            />
            <span className="pcv-brief-textarea-counter">
              {userIntent.length}/{INTENT_MAX_LENGTH}
            </span>
            <div className="pcv-brief-chip-row">
              <span className="pcv-brief-section-label" style={{ width: '100%', marginBottom: 4 }}>
                快捷灵感（点击填入，不会自动提交）
              </span>
              {INTENT_EXAMPLES.map((ex) => (
                <span key={ex} className="pcv-brief-chip nodrag" onClick={() => appendExample(ex)}>
                  {ex}
                </span>
              ))}
            </div>
          </div>
          {!isBusy && !currentResult ? <span className="pcv-brief-generate-hint">基于你的输入，生成一个创意方向</span> : null}

          {/* ⑥ Footer — expanded only (COLLAPSED_SHOWS_FOOTER=NO). */}
          <div className="pcv-gen-footer nodrag" onClick={(e) => e.stopPropagation()}>
            <Tooltip title={providerSelectOptions.find((o) => o.value === resolvedProviderOptionId)?.title || 'Provider'} mouseEnterDelay={0.4}>
              <CanvasSelect
                size="small"
                className="pcv-gen-field-select pcv-gen-footer-select nodrag"
                popupClassName="pcv-gen-field-select-popup"
                data-testid="ideation-provider-select"
                value={resolvedProviderOptionId ?? undefined}
                disabled={providerOptionsError || providerSelectOptions.length === 0}
                placeholder={providerOptionsError ? '加载失败' : 'Provider'}
                options={providerSelectOptions}
                onChange={(v) => patchFields(buildExplicitProviderSelectionPatch(v))}
                popupMatchSelectWidth={false}
                placement="bottomLeft"
                getPopupContainer={() => document.body}
              />
            </Tooltip>
            <CanvasSelect
              size="small"
              className="pcv-gen-field-select pcv-gen-footer-select nodrag"
              popupClassName="pcv-gen-field-select-popup"
              value={skillSelectionSource === 'EXPLICIT' ? 'creative_ideation' : 'AUTO'}
              options={[
                { value: 'AUTO', label: '自动（推荐）' },
                // Display name only. Tenant canvas has no workspace-scoped
                // Skill list API yet (platform /platform/skills is admin-only).
                { value: 'creative_ideation', label: '创意生成 v1' },
              ]}
              onChange={(v) => patchFields(v === 'AUTO' ? { skillSelectionSource: 'AUTO', skillKey: null } : { skillSelectionSource: 'EXPLICIT', skillKey: v })}
              popupMatchSelectWidth={false}
              placement="bottomLeft"
              getPopupContainer={() => document.body}
            />
            <Tooltip title="发散生成 — 基于当前方向一次生成 3 个有明显差异的新创意节点">
              <button
                type="button"
                ref={setDivergeBtnEl}
                className="pcv-gen-footer-diverge-btn pcv-gen-footer-diverge-btn--labeled"
                disabled={!canDiverge}
                onClick={(e) => {
                  e.stopPropagation()
                  setDivergeOpen(true)
                }}
              >
                {isDivergeBusy ? <LoadingOutlined spin /> : <BranchesOutlined />} 发散生成
              </button>
            </Tooltip>
            <button type="button" className="pcv-gen-footer-generate-btn" disabled={!canGenerate} onClick={() => nodeData.onGenerateCreative?.(id)}>
              {isGenerateBusy ? <LoadingOutlined spin /> : null} {isGenerateBusy ? '生成中…' : currentResult ? '重新生成' : '生成创意'}
            </button>
          </div>
        </div>
      ) : null}

      <CanvasActionPopover anchorEl={divergeBtnEl} open={divergeOpen} onClose={() => setDivergeOpen(false)} title="发散生成" width={260}>
        <div className="pcv-candidate-expand-popover">
          <span style={{ fontSize: 11, color: 'var(--pcv-text-tertiary)' }}>基于当前方向生成 3 个有明显差异的新方向，想往什么方向变化？（可选）</span>
          <textarea
            className="pcv-candidate-expand-input nodrag"
            placeholder="例如：更搞笑、更电影感、不要秘密基地……"
            value={divergeInstruction}
            onChange={(e) => setDivergeInstruction(e.target.value)}
          />
          <div className="pcv-candidate-expand-actions">
            <button type="button" className="pcv-candidate-action-btn" onClick={() => setDivergeOpen(false)}>
              取消
            </button>
            <button type="button" className="pcv-candidate-action-btn pcv-candidate-action-btn--primary" onClick={() => submitDiverge(divergeInstruction.trim() || null)}>
              生成 3 个新方向
            </button>
          </div>
        </div>
      </CanvasActionPopover>

      <CanvasActionPopover anchorEl={historyBtnEl} open={historyOpen} onClose={() => setHistoryOpen(false)} title="生成历史" width={280}>
        {historyLoading ? (
          <div className="pcv-gen-history-empty">
            <LoadingOutlined spin /> 加载中…
          </div>
        ) : (
          <HistoryList entries={historyEntries || []} />
        )}
      </CanvasActionPopover>

      {/* External Quick Actions — CARD RIGHT OUTSIDE the Result region
       * (spec Gate E "由 RESULT DOM OWN POSITIONING"), never nested
       * inside `.pcv-gen-result-current` content. Admin-driven (Gate G) —
       * renders nothing until Platform Admin has this action enabled for
       * `generate_creative`. */}
      {!isBusy && currentResult && nodeData.onQuickCreateScriptFromCreative ? (
        <CanvasResultQuickActionRail
          sourceNodeType="generate_creative"
          className="pcv-media-workflow-toolbar--generate-creative"
          onMouseEnter={() => setResultHovered(true)}
          onMouseLeave={() => setResultHovered(false)}
          actionHandlers={[
            {
              targetNodeType: 'script_generation',
              tooltip: '生成剧本 — 用这个创意结果作为创意上下文',
              onClick: handleQuickCreateScript,
            },
          ]}
        />
      ) : null}
    </div>
  )
}

export default memo(GenerateCreativeNodeImpl)
